import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EmptyState } from '@vd/ui';
import { Search as SearchIcon } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../features/auth/AuthProvider';
import { When } from '../lib/When';
import { useTabTitle, useTabWorkspaceName } from '../lib/tabTitle';

export function SearchPage() {
  // This tab's title (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({ parts: [tabWorkspace, "Search"] });
  const [params] = useSearchParams();
  const { projectId } = useParams();
  const { currentCompanyId } = useAuth();
  const navigate = useNavigate();
  const q = params.get('q')?.trim() ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['search', q, projectId ?? null, currentCompanyId],
    queryFn: () => api.searchTickets(q, {
      companyId: projectId ? undefined : (currentCompanyId ?? undefined),
      projectId: projectId ?? undefined,
      limit: 50,
    }),
    enabled: q.length >= 2,
  });

  const items = data?.items ?? [];

  return (
    <div style={{ padding: '24px 40px', width: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Search</h1>
        {q && (
          <span style={{ color: 'var(--text-secondary)' }}>
            for <strong style={{ color: 'var(--text)' }}>{q}</strong>
            {projectId && <span> in this project</span>}
          </span>
        )}
      </div>

      {q.length < 2 ? (
        <EmptyState
          icon={<SearchIcon size={28} />}
          title="Type a query to begin"
          description="Tickets are searchable by title, description, comments, and source URL."
        />
      ) : isLoading ? (
        <div style={{ color: 'var(--text-tertiary)' }}>Searching…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<SearchIcon size={28} />}
          title={`No results for “${q}”`}
          description="Try a shorter or different query."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            {items.length} {items.length === 1 ? 'result' : 'results'}
          </p>
          {items.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate(`/tickets/${t.id}`)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                gap: 4, padding: '12px 16px', textAlign: 'left',
                background: 'var(--surface)',
                border: '1px solid var(--border)', borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--text)',
              }}
            >
              <div style={{ fontWeight: 500 }}>{t.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', gap: 12 }}>
                <span>{t.id}</span>
                <When at={t.createdAt} exact />
                {t.sourceUrl && <span style={{ fontFamily: 'ui-monospace, monospace' }}>{t.sourceUrl}</span>}
                {t.hasAiActivity && <span style={{ color: 'var(--accent-mcp, var(--accent))' }}>AI activity</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
